import { useEffect, useRef, useState } from 'react';

import { type IAssignedAgent } from '../../../../interface/agent-assignment';
import { type IAgentLocationDto } from '../../../../interface/child-agent';
import { getLatestAgentLocation } from '../../../../lib/agent-track-api';
import LocationMap from '../../../utils/location-map';
import { RefreshSVG } from '../../../utils/svgs';
import { type TabHint } from '../ChildAgentTabs';
import { asBool, relativeTime } from '../utils';
import TabMessage from './TabMessage';

type ViewState = 'loading' | 'ready' | 'locked' | 'empty' | 'error';

const LockGlyph = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<rect
			x='5'
			y='10.5'
			width='14'
			height='9.5'
			rx='2'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
		<path
			d='M8 10.5V8a4 4 0 0 1 8 0v2.5'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
	</svg>
);
const PinGlyph = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
		<circle
			cx='12'
			cy='10'
			r='2.5'
			stroke='currentColor'
			strokeWidth='1.6'
		/>
	</svg>
);
const WarnGlyph = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

interface IMapTabProps {
	entityCode: string;
	agent: IAssignedAgent | null;
	onHint: (hint: TabHint | undefined) => void;
}

/**
 * Tab 4 — the child agent's live GPS location. The read endpoint is
 * administrator-only today and 403s for party-agent tokens, so a 403 renders a
 * calm "not yet enabled" lock state (distinct from a genuine error). When the
 * backend gate is relaxed to party scope, this tab works unchanged.
 */
const MapTab = ({ entityCode, agent, onHint }: IMapTabProps) => {
	const [state, setState] = useState<ViewState>('loading');
	const [loc, setLoc] = useState<IAgentLocationDto | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const loadedRef = useRef(false);

	const pickLocation = (rows: IAgentLocationDto[]): IAgentLocationDto | null => {
		if (!rows.length) return null;
		if (agent?.userId) {
			const mine = rows.find((r) => String(r.appUserId) === String(agent.userId));
			if (mine) return mine;
		}
		return rows[0];
	};

	const load = (isRefresh = false) => {
		if (isRefresh) setRefreshing(true);
		else setState('loading');

		getLatestAgentLocation(entityCode).then((outcome) => {
			setRefreshing(false);

			if (outcome.reachable && outcome.httpStatus === 403) {
				setState('locked');
				onHint({ tone: 'neutral', locked: true });
				return;
			}
			if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300) {
				setState('error');
				onHint({ tone: 'danger' });
				return;
			}
			const found = pickLocation(outcome.body?.data ?? []);
			if (!found) {
				setState('empty');
				onHint({ tone: 'neutral' });
				return;
			}
			setLoc(found);
			setState('ready');
			onHint(asBool(found.isStale) ? { tone: 'warning' } : { tone: 'success', live: true });
		});
	};

	useEffect(() => {
		if (loadedRef.current) return;
		loadedRef.current = true;
		load();
	}, []);

	if (state === 'loading') {
		return (
			<div className='ca-map__skeleton f-column-15'>
				<div className='ca-map__sk-map skeleton' />
				<div className='ca-map__sk-pill skeleton' />
			</div>
		);
	}

	if (state === 'locked') {
		const phone = agent?.phoneNumber?.trim();
		return (
			<TabMessage
				icon={<LockGlyph />}
				tone='lock'
				title='Live location isn’t enabled for your account yet'
				hint='Real-time agent locations are being rolled out for party supervisors. In the meantime you can reach the agent directly.'
				action={phone ? { label: 'Call agent', href: `tel:${phone}` } : undefined}
			/>
		);
	}

	if (state === 'empty') {
		return (
			<TabMessage
				icon={<PinGlyph />}
				tone='neutral'
				title='No recent location'
				hint='This agent hasn’t shared a location ping recently.'
				action={{ label: 'Refresh', onClick: () => load() }}
			/>
		);
	}

	if (state === 'error' || !loc) {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title='We couldn’t load the live location.'
				hint='The location service is temporarily unavailable.'
				action={{ label: 'Try again', onClick: () => load() }}
			/>
		);
	}

	const stale = asBool(loc.isStale);
	const seen = relativeTime(loc.updatedAt || loc.clientTimestamp);

	return (
		<div className='ca-map f-column-15'>
			<div className={`ca-map__status ca-map__status--${stale ? 'stale' : 'live'}`}>
				<span
					className='ca-map__dot'
					aria-hidden='true'
				/>
				<span className='ca-map__status-text'>
					{stale ? 'Last known location' : 'Live location'}
					{seen ? ` · updated ${seen}` : ''}
				</span>
				<button
					type='button'
					className={`ca-map__refresh ${refreshing ? 'is-spinning' : ''}`}
					onClick={() => load(true)}
					aria-label='Refresh location'
					disabled={refreshing}
				>
					<RefreshSVG />
				</button>
			</div>

			<div className='ca-map__canvas'>
				<LocationMap
					latitude={loc.latitude}
					longitude={loc.longitude}
					height='280px'
				/>
			</div>

			<div className='ca-map__meta'>
				{typeof loc.accuracy === 'number' ? (
					<span className='ca-map__meta-item'>Accuracy ±{Math.round(loc.accuracy)} m</span>
				) : null}
				<span className='ca-map__meta-item ca-map__meta-item--coords'>
					{loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
				</span>
			</div>
		</div>
	);
};

export default MapTab;
